import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  CloseButton,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@/ui-components/button-with-loading";
import { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { getWatchlistType } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { useMemo } from "react";

interface ViewAdvancedFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filter: CategoryQuery | null;
}

// Build a lookup map from tag ID to tag name
const tagIdToName: Record<string, string> = {};
menuCategories.forEach((category) => {
  category.items.forEach((item) => {
    tagIdToName[String(item.id)] = item.title;
  });
});

const ViewAdvancedFilterModal: React.FC<ViewAdvancedFilterModalProps> = ({
  isOpen,
  onClose,
  filter,
}) => {
  if (!filter) return null;

  const tickerWatchlists = useMemo(
    () => (filter.portfolios || []).filter((p) => getWatchlistType(p) === "ticker-only"),
    [filter.portfolios]
  );
  const keywordWatchlists = useMemo(
    () => (filter.portfolios || []).filter((p) => getWatchlistType(p) === "keyword-only"),
    [filter.portfolios]
  );
  const mixedWatchlists = useMemo(
    () => (filter.portfolios || []).filter((p) => getWatchlistType(p) === "mixed"),
    [filter.portfolios]
  );

  const hasKeyword = filter.searchKeyword && filter.searchKeyword.trim().length > 0;
  const hasTags = filter.categories && filter.categories.length > 0;
  const hasTagsWatchlists = (filter.tagsWatchlists?.length ?? 0) > 0;

  return (
    <Dialog open={isOpen} onClose={onClose} className="no-drag">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/90 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in z-10"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-gray-800 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <CloseButton
              as={Button}
              variant="secondary"
              className="absolute right-3 top-3 z-50"
              renderIcon={() => <XMarkIcon className="h-5 w-5" />}
            >
              <span className="sr-only">Close</span>
            </CloseButton>

            <div className="p-6">
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-xl font-semibold text-gray-100">
                    {filter.queryName}
                  </h2>
                  <span className="text-xs px-2 py-0.5 rounded bg-squawk-blue/20 text-squawk-blue border border-squawk-blue/30">
                    Newsquawk
                  </span>
                </div>
                <p className="text-sm text-gray-400">Advanced Filter</p>
              </div>

              {/* Content sections */}
              <div className="space-y-4 max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                {/* Ticker Watchlists */}
                {tickerWatchlists.length > 0 && (
                  <div className="border border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                      <span className="text-sm font-medium text-gray-300">
                        Ticker Watchlists
                      </span>
                    </div>
                    <div className="p-4 bg-gray-900/50 space-y-3">
                      {tickerWatchlists.map((portfolio) => (
                        <div key={portfolio.id}>
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className="w-3 h-3 rounded ring-1 ring-black/10"
                              style={{ backgroundColor: portfolio.color }}
                            />
                            <span className="text-xs font-medium text-gray-200">
                              {portfolio.name}
                            </span>
                            <span className="text-2xs text-gray-500">
                              {portfolio.tickers.length} tickers
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {portfolio.tickers.map((ticker, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-900/30 text-blue-300 border border-blue-700/50"
                              >
                                {ticker}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Keyword Watchlists */}
                {keywordWatchlists.length > 0 && (
                  <div className="border border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                      <span className="text-sm font-medium text-gray-300">
                        Keyword Watchlists
                      </span>
                    </div>
                    <div className="p-4 bg-gray-900/50 space-y-3">
                      {keywordWatchlists.map((portfolio) => (
                        <div key={portfolio.id}>
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className="w-3 h-3 rounded ring-1 ring-black/10"
                              style={{ backgroundColor: portfolio.color }}
                            />
                            <span className="text-xs font-medium text-gray-200">
                              {portfolio.name}
                            </span>
                            <span className="text-2xs text-gray-500">
                              {(portfolio.keywords || []).length} keywords
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {(portfolio.keywords || []).map((keyword, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-900/30 text-purple-300 border border-purple-700/50"
                              >
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mixed Watchlists (legacy) */}
                {mixedWatchlists.length > 0 && (
                  <div className="border border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                      <span className="text-sm font-medium text-gray-300">
                        Mixed Watchlists
                      </span>
                    </div>
                    <div className="p-4 bg-gray-900/50 space-y-3">
                      {mixedWatchlists.map((portfolio) => (
                        <div key={portfolio.id}>
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className="w-3 h-3 rounded ring-1 ring-black/10"
                              style={{ backgroundColor: portfolio.color }}
                            />
                            <span className="text-xs font-medium text-gray-200">
                              {portfolio.name}
                            </span>
                          </div>
                          {portfolio.tickers.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {portfolio.tickers.map((ticker, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-900/30 text-blue-300 border border-blue-700/50"
                                >
                                  {ticker}
                                </span>
                              ))}
                            </div>
                          )}
                          {(portfolio.keywords || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {(portfolio.keywords || []).map((keyword, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-900/30 text-purple-300 border border-purple-700/50"
                                >
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Keyword */}
                {hasKeyword && (
                  <div className="border border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                      <span className="text-sm font-medium text-gray-300">
                        Search Keyword
                      </span>
                    </div>
                    <div className="p-4 bg-gray-900/50">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-amber-900/30 text-amber-300 border border-amber-700/50">
                        "{filter.searchKeyword}"
                      </span>
                    </div>
                  </div>
                )}

                {/* Tags */}
                {hasTags && (
                  <div className="border border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                      <span className="text-sm font-medium text-gray-300">
                        Tags
                      </span>
                      <span className="ml-2 text-2xs text-gray-500">
                        {filter.categories.length} {filter.categories.length === 1 ? "tag" : "tags"}
                      </span>
                    </div>
                    <div className="p-4 bg-gray-900/50">
                      <div className="flex flex-wrap gap-1.5">
                        {filter.categories.map((tagId, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-900/30 text-green-300 border border-green-700/50"
                          >
                            {tagIdToName[tagId] || tagId}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tag Watchlists */}
                {hasTagsWatchlists && (
                  <div className="border border-gray-700 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                      <span className="text-sm font-medium text-gray-300">
                        Tag Watchlists
                      </span>
                      <span className="ml-2 text-2xs text-gray-500">
                        {filter.tagsWatchlists!.length} {filter.tagsWatchlists!.length === 1 ? "watchlist" : "watchlists"}
                      </span>
                    </div>
                    <div className="p-4 bg-gray-900/50 space-y-3">
                      {filter.tagsWatchlists!.map((tw) => (
                        <div key={tw.id}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-medium text-gray-200">
                              {tw.name}
                            </span>
                            <span className="text-2xs text-gray-500">
                              {tw.tags.length} tags
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {tw.tags.map((tagId, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-900/30 text-green-300 border border-green-700/50"
                              >
                                {tagIdToName[tagId] || tagId}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Logic / Operators */}
                <div className="border border-gray-700 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-700">
                    <span className="text-sm font-medium text-gray-300">
                      Filter Logic
                    </span>
                  </div>
                  <div className="p-4 bg-gray-900/50 flex flex-wrap gap-2">
                    {filter.filterMode && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-300">
                        <span className="text-gray-500">Watchlists + Tags:</span>
                        <span className="px-1.5 py-0.5 rounded bg-gray-700 text-gray-200 font-mono text-2xs">
                          {filter.filterMode}
                        </span>
                      </span>
                    )}
                    {filter.keywordOp && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-300">
                        <span className="text-gray-500">Keyword:</span>
                        <span className="px-1.5 py-0.5 rounded bg-gray-700 text-gray-200 font-mono text-2xs">
                          {filter.keywordOp}
                        </span>
                      </span>
                    )}
                    {filter.keywordWatchlistOp && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-300">
                        <span className="text-gray-500">Keyword Watchlist:</span>
                        <span className="px-1.5 py-0.5 rounded bg-gray-700 text-gray-200 font-mono text-2xs">
                          {filter.keywordWatchlistOp}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-end">
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default ViewAdvancedFilterModal;
